<template>
  <v-form
    class="ade-forms-edit"
    @submit.prevent="submit"
    :key="id"
    autocomplete="off"
  >
    <div class="ade-forms-edit__fields-wrapper">
      <v-container class="pb-0">
        <v-col>
          <h3 class="headline">
            {{
              isEdition
                ? $t("Modifier un utilisateur")
                : $t("Créer un utilisateur")
            }}
          </h3>
        </v-col>
        <v-col>
          <v-text-field
            v-model="email"
            :label="$t(`Email*`)"
            type="email"
            filled
            :hide-details="isEmailAvailable"
            :error="!isEmailAvailable"
            :error-messages="isEmailAvailableMessage"
            @input="checkEmailAvailability"
          >
            <template v-slot:message="{ key, message }">
              <div class="ade-forms-edit__email-message">
                {{ message }}
                <create-button
                  :entity="existingEmailUser"
                  :tmpEntity="true"
                  class="ade-forms-edit__update-existing ml-1"
                >
                  <template v-slot:default="{ on, loading }">
                    <v-btn
                      @click="
                        () => {
                          on().then(() => resetData());
                        }
                      "
                      :disabled="!existingEmailUser"
                      :loading="loading"
                      small
                      text
                      color="primary"
                    >
                      {{ $t("Modifier l'utilisateur") }}
                    </v-btn>
                  </template>
                </create-button>
              </div>
            </template>
          </v-text-field>
        </v-col>
        <v-subheader>
          {{ $t("Sociétés") }}
        </v-subheader>

        <v-col>
          <v-autocomplete
            v-model="agences"
            :label="$t(`Chercher une société`)"
            :items="linkedCompanySearchList"
            :search-input.sync="linkedCompanySearch"
            :loading="isLinkedCompanySearchLoading"
            no-filter
            item-value="id"
            item-text="nomComplet"
            append-icon="mdi-magnify"
            filled
            :hide-no-data="
              !linkedCompanySearch ||
                !linkedCompanySearch.length ||
                isLinkedCompanySearchLoading
            "
            autocomplete="off"
            hide-selected
            return-object
            multiple
            :menu-props="{ closeOnContentClick: true }"
            ref="companiesAutocomplete"
            @change="onSelectCompany"
            :no-data-text="
              $t('Aucune société ne correspond à votre recherche...')
            "
            class="no-icon-rotation"
            hide-details
          >
            <template v-slot:selection />
          </v-autocomplete>
          <div class="ade-forms-edit__chip-list">
            <v-chip
              v-for="company in agences"
              :key="company.id"
              :close="!company.readOnly"
              @click:close="removeFromAgences(company)"
              class="mt-2 ade-forms-edit__listable-chip"
            >
              {{ company.nomComplet }}
            </v-chip>
          </div>
        </v-col>
        <v-col v-if="hasAdminRights">
          <v-select
            v-model="agenceReference"
            :label="$t(`Société de référence`)"
            :items="agences"
            no-filter
            item-value="id"
            item-text="nomComplet"
            filled
            :hide-no-data="!agences.length"
            :no-data-text="$t('Veuillez d\'abord sélectionner des sociétés.')"
            return-object
            hide-details
          />
        </v-col>
        <v-subheader>
          {{ $t("Informations") }}
        </v-subheader>
        <v-col>
          <v-text-field
            v-model="prenom"
            :label="$t(`Prénom`)"
            filled
            hide-details
          />
        </v-col>
        <v-col>
          <v-text-field v-model="nom" :label="$t(`Nom`)" filled hide-details />
        </v-col>
        <v-col>
          <v-text-field
            v-model="fonction"
            :label="$t(`Fonction`)"
            filled
            hide-details
          />
        </v-col>
        <v-col v-if="hasAdminRights">
          <v-select
            v-model="persona"
            :label="$t(`Persona`)"
            :items="personaList"
            no-filter
            item-value="id"
            item-text="nom"
            filled
            :no-data-text="$t('Aucun personna disponible')"
            return-object
            hide-details
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="numTel"
            type="tel"
            :label="$t(`Numéro de téléphone`)"
            filled
            hide-details
          />
        </v-col>
        <!-- <v-col>
          <v-select
            v-model="active"
            :items="[
              { text: $t(`Actif`), value: true },
              { text: $t(`Inactif`), value: false }
            ]"
            filled
            :label="$t(`Status`)"
            hide-details
            disabled
            hidden
          />
        </v-col> -->
        <v-col>
          <v-container>
            <v-checkbox
              v-model="contactMiseAJour"
              :label="$t(`Contact de mise à jour`)"
              hide-details
              class="mt-0"
            />
            <v-checkbox
              v-model="contactFacturation"
              :label="$t(`Contact de facturation`)"
              hide-details
            />
            <v-checkbox
              v-model="contactSignataire"
              :label="$t(`Contact signataire`)"
              hide-details
            />
          </v-container>
        </v-col>
      </v-container>

      <v-container class="pt-0">
        <v-subheader>
          {{ $t("Applications") }}
          <v-progress-circular
            v-if="accesLoading"
            class="ml-2"
            indeterminate
            color="primary"
          />
        </v-subheader>
        <v-subheader v-if="acces.length == 0 && !accesLoading" class="caption">
          Aucune application disponible.
        </v-subheader>
        <v-col v-else-if="!accesLoading">
          <v-expansion-panels v-model="panelApp">
            <v-expansion-panel
              v-model="panelApp"
              class="top-level-extansion-panel"
              active-class="extantion-panel-selected"
              v-for="(applicationAccesList, i) in groupedApplications"
              :key="i"
              @click="initAgenceSelected(applicationAccesList)"
            >
              <v-expansion-panel-header>
                <v-checkbox
                  @click.native="expansionCheckboxClick($event)"
                  @change="toggleAccesApp(applicationAccesList, $event)"
                  hide-details
                  :input-value="
                    getApplicationCheckboxState(applicationAccesList)
                  "
                  :indeterminate="
                    getApplicationCheckboxState(applicationAccesList) === -1
                  "
                  class="mt-0 pt-0 mr-5"
                ></v-checkbox>
                {{ applicationAccesList[0].abonnement.application.nom }}
                <v-tooltip
                  bottom
                  class="icon-expanded"
                  v-if="
                    applicationAccesList[0].abonnement.application.monoAgence
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="primary"
                      dark
                      v-bind="attrs"
                      v-on="on"
                      class="icon-expanded"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <div>
                    <b>Mono-Agence</b> <br />
                    Application ne gérant les droits que d'une seule société
                  </div>
                </v-tooltip>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-col>
                  <v-select
                    v-if="
                      applicationAccesList[0].abonnement.application.monoAgence
                    "
                    v-model="agenceSelected"
                    :label="$t(`Société`)"
                    :items="agences"
                    no-filter
                    item-value="id"
                    item-text="nomComplet"
                    filled
                    :hide-no-data="!agences.length"
                    :no-data-text="
                      $t('Veuillez d\'abord sélectionner des sociétés.')
                    "
                    return-object
                    hide-details
                  />
                </v-col>
                <v-expansion-panels
                  v-for="(groupedAccesData, j) in groupedAccesByAgence(
                    applicationAccesList
                  )"
                  :key="'' + i + j"
                  dense
                >
                  <v-subheader class="px-6">
                    {{ groupedAccesData.agence.nomComplet }}
                  </v-subheader>
                  <!-- Informations lié à la campagne de MAJ -->
                  <v-col
                    v-if="
                      hasAdminRights &&
                        applicationAccesList[0].abonnement.application.nom.includes(
                          'mise à jour'
                        )
                    "
                    class="contact-maj"
                  >
                    <v-btn-toggle
                      v-model="adherentNature"
                      color="primary"
                      group
                      :class="{ 'disable-events': true }"
                      active-class="adherent-selected"
                      v-if="isEdition"
                    >
                      <v-btn value="interne" class="adherent"> Interne </v-btn>

                      <v-btn value="partenaire" class="adherent">
                        Partenaire
                      </v-btn>

                      <v-btn value="adhérent" class="adherent">
                        Adherent
                      </v-btn>
                    </v-btn-toggle>

                    <v-list :disabled="true" :dense="true" v-if="isAdherent">
                      <v-list-item v-for="(item, i) in adherentScope" :key="i">
                        <v-list-item-title v-html="item"></v-list-item-title>
                      </v-list-item>
                    </v-list>

                    <v-textarea
                      v-model="commentaireMaj"
                      :label="$t(`Commentaire de mise à jour`)"
                    />

                    <v-menu offset-y min-width="290px" v-if="hasAdminRights">
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :label="$t(`Date de fin de mise à jour`)"
                          :value="formatDate(new Date(dateFinMaj))"
                          filled
                          hide-details
                          append-icon="mdi-calendar"
                          readonly
                          clearable
                          v-on="on"
                          class="dateFinMaj"
                        />
                      </template>
                      <v-date-picker
                        @change="dateFinMaj = new Date($event).toISOString()"
                        locale="fr-FR"
                        first-day-of-week="1"
                        no-title
                      />
                    </v-menu>
                  </v-col>
                  <template
                    v-for="(accesByDroit, k) in groupedAccesData.accesByDroit"
                  >
                    <v-expansion-panel
                      :key="'' + i + j + k"
                      :readonly="accesByDroit.length <= 1"
                    >
                      <v-expansion-panel-header
                        class="px-6"
                        :disabled="accesByDroit.length <= 1"
                        :hide-actions="accesByDroit.length <= 1"
                      >
                        <v-checkbox
                          @click.native="expansionCheckboxClick($event)"
                          @change="toggleAccesRight(accesByDroit, $event)"
                          :color="getCheckboxColor(accesByDroit[0].droit.code)"
                          :input-value="
                            getApplicationCheckboxState(accesByDroit)
                          "
                          :indeterminate="
                            getApplicationCheckboxState(accesByDroit) === -1
                          "
                          hide-details
                          class="mt-0 pt-0 mr-5"
                        />
                        <div>
                          <div class="ade-forms-edit__small-title">
                            {{ accesByDroit[0].droit.nom }}
                            <span
                              v-if="
                                accesByDroit.length == 1 &&
                                  !accesByDroit[0].perimetre
                              "
                            >
                              <v-btn
                                icon
                                v-if="
                                  hasAdminRights &&
                                    accesByDroit[0].abonnement.application
                                      .useDateExpiration &&
                                    accesByDroit[0].used &&
                                    !accesByDroit[0].expirationDate
                                "
                                x-small
                                @click="displayExpirationDate(accesByDroit[0])"
                              >
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon dark v-bind="attrs" v-on="on"
                                      >mdi-account-clock</v-icon
                                    >
                                  </template>
                                  <span>Ajouter une date d'expiration</span>
                                </v-tooltip>
                              </v-btn>

                              <v-menu
                                offset-y
                                min-width="290px"
                                v-if="
                                  hasAdminRights &&
                                    accesByDroit[0].abonnement.application
                                      .useDateExpiration &&
                                    accesByDroit[0].used &&
                                    (accesByDroit[0].expirationDate ||
                                      accesByDroit[0].displayExpirationDate)
                                "
                              >
                                <template v-slot:activator="{ on }">
                                  <v-text-field
                                    :label="$t(`Date d'expiration`)"
                                    :value="
                                      formatDate(
                                        new Date(accesByDroit[0].expirationDate)
                                      )
                                    "
                                    filled
                                    hide-details
                                    append-icon="mdi-calendar"
                                    readonly
                                    clearable
                                    v-on="on"
                                    class="expirationDate"
                                    @click:clear="
                                      setExpirationDate(accesByDroit[0], null)
                                    "
                                  />
                                </template>
                                <v-date-picker
                                  @change="
                                    setExpirationDate(accesByDroit[0], $event)
                                  "
                                  locale="fr-FR"
                                  first-day-of-week="1"
                                  no-title
                                />
                              </v-menu>
                            </span>
                          </div>
                          <div
                            v-if="
                              accesByDroit.length == 1 &&
                                accesByDroit[0].perimetre
                            "
                            style="white-space: normal"
                            class="ade-forms-edit__small-text"
                          >
                            {{ accesByDroit[0].perimetre.nom }}
                            <v-btn
                              icon
                              v-if="
                                hasAdminRights &&
                                  accesByDroit[0].abonnement.application
                                    .useDateExpiration &&
                                  accesByDroit[0].used &&
                                  !accesByDroit[0].expirationDate
                              "
                              x-small
                              @click="displayExpirationDate(accesByDroit[0])"
                            >
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon dark v-bind="attrs" v-on="on"
                                    >mdi-account-clock</v-icon
                                  >
                                </template>
                                <span>Ajouter une date d'expiration</span>
                              </v-tooltip>
                            </v-btn>

                            <v-menu
                              offset-y
                              min-width="290px"
                              v-if="
                                hasAdminRights &&
                                  accesByDroit[0].abonnement.application
                                    .useDateExpiration &&
                                  accesByDroit[0].used &&
                                  (accesByDroit[0].expirationDate ||
                                    accesByDroit[0].displayExpirationDate)
                              "
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  :label="$t(`Date d'expiration`)"
                                  :value="
                                    formatDate(
                                      new Date(accesByDroit[0].expirationDate)
                                    )
                                  "
                                  filled
                                  hide-details
                                  append-icon="mdi-calendar"
                                  readonly
                                  clearable
                                  v-on="on"
                                  class="expirationDate"
                                  @click:clear="
                                    setExpirationDate(accesByDroit[0], null)
                                  "
                                />
                              </template>
                              <v-date-picker
                                @change="
                                  setExpirationDate(accesByDroit[0], $event)
                                "
                                locale="fr-FR"
                                first-day-of-week="1"
                                no-title
                              />
                            </v-menu>
                          </div>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content v-if="accesByDroit.length > 1">
                        <v-list dense>
                          <v-list-item
                            v-for="(singularAcces, l) in accesByDroit"
                            :key="'' + i + j + k + l"
                          >
                            <v-list-item-icon class="mr-5">
                              <v-checkbox
                                v-model="singularAcces.used"
                                color="rgba(0, 188, 212, 0.5)"
                                hide-details
                                class="mt-0 pt-0"
                              />
                            </v-list-item-icon>
                            <v-list-item-content v-if="accesByDroit.length > 1">
                              <v-list-item-subtitle
                                >{{
                                  singularAcces.perimetre
                                    ? singularAcces.perimetre.nom
                                    : $t("Paramètre indéfini")
                                }}
                                <v-btn
                                  icon
                                  v-if="
                                    hasAdminRights &&
                                      singularAcces.abonnement.application
                                        .useDateExpiration &&
                                      singularAcces.abonnement.application
                                        .useDateExpiration &&
                                      singularAcces.used &&
                                      !singularAcces.expirationDate
                                  "
                                  x-small
                                  @click="displayExpirationDate(singularAcces)"
                                >
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-icon dark v-bind="attrs" v-on="on"
                                        >mdi-account-clock</v-icon
                                      >
                                    </template>
                                    <span>Ajouter une date d'expiration</span>
                                  </v-tooltip>
                                </v-btn>

                                <v-menu
                                  offset-y
                                  min-width="290px"
                                  v-if="
                                    hasAdminRights &&
                                      singularAcces.abonnement.application
                                        .useDateExpiration &&
                                      singularAcces.used &&
                                      (singularAcces.expirationDate ||
                                        singularAcces.displayExpirationDate)
                                  "
                                >
                                  <template v-slot:activator="{ on }">
                                    <v-text-field
                                      :label="$t(`Date d'expiration`)"
                                      :value="
                                        formatDate(
                                          new Date(singularAcces.expirationDate)
                                        )
                                      "
                                      filled
                                      hide-details
                                      append-icon="mdi-calendar"
                                      readonly
                                      clearable
                                      v-on="on"
                                      class="expirationDate"
                                      @click:clear="
                                        setExpirationDate(singularAcces, null)
                                      "
                                    />
                                  </template>
                                  <v-date-picker
                                    @change="
                                      setExpirationDate(singularAcces, $event)
                                    "
                                    locale="fr-FR"
                                    first-day-of-week="1"
                                    no-title
                                  />
                                </v-menu>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-content v-else>
                              <v-list-item-title>{{
                                singularAcces.droit.nom
                              }}</v-list-item-title>
                              <v-list-item-subtitle
                                v-if="singularAcces.perimetre"
                                style="white-space: normal"
                                >{{ singularAcces.perimetre.nom }}
                              </v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </template>
                </v-expansion-panels>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-container>
    </div>

    <div class="ade-forms-edit-footer">
      <v-divider />
      <v-container class="d-flex justify-space-between">
        <v-btn color="grey" rounded depressed text @click="closeDrawer">
          {{ $t("Annuler") }}
        </v-btn>
        <create-button v-if="!isEdition">
          <template v-slot:default="{ on }">
            <v-btn
              color="primary"
              rounded
              depressed
              text
              :loading="loading"
              :disabled="
                !isEmailAvailable || !formIsValid || (!hasRight && !isEdition)
              "
              @click="submit({ callback: on })"
            >
              {{ $t("Créer et ajouter un autre") }}
            </v-btn>
          </template>
        </create-button>
        <v-btn
          type="submit"
          rounded
          color="primary"
          depressed
          :loading="loading"
          :disabled="
            !isEmailAvailable || !formIsValid || (!hasRight && !isEdition)
          "
        >
          {{ isEdition ? $t("Mettre à jour") : $t("Créer") }}
        </v-btn>
      </v-container>
    </div>
  </v-form>
</template>

<script>
import _ from "lodash";
import dateFormat from "dateformat";

import CreateButton from "@/components/users/CreateButton";

import { createNamespacedHelpers } from "vuex";
import {
  FETCH,
  SUBMIT,
  IS_LOGIN_AVAILABLE,
  FETCH_USER_AVAILABLE_ACCES
} from "@/store/action-types";

import { RepositoryFactory, USERS_REPOSITORY } from "@/utils/RepositoryFactory";
const UsersRepository = RepositoryFactory.get(USERS_REPOSITORY);

import { mapAppFields } from "@/store/modules/app";

import {
  mapUserCreateFormFields,
  mapUserCreateFormMultiRowFields
} from "@/store/modules/user";

const { mapGetters: mapAuthGetters } = createNamespacedHelpers(`auth`);

const { mapActions: mapUserActions } = createNamespacedHelpers(`user`);
const { mapActions: mapUserCreateFormActions } = createNamespacedHelpers(
  `user/createForm`
);

const { mapActions: mapCompanyActions } = createNamespacedHelpers(`company`);

const { mapState: mapPersonaState } = createNamespacedHelpers(`persona`);

export default {
  components: {
    CreateButton
  },
  data: () => ({
    loading: false,
    referenceCompanySearch: null,
    isReferenceCompanySearchLoading: false,
    referenceCompanySearchListBase: [],
    linkedCompanySearch: null,
    isLinkedCompanySearchLoading: false,
    linkedCompanySearchListBase: [],
    isEmailAvailable: true,
    existingEmailUser: null,
    UserGetByLoginCall: null,
    accesLoading: false,
    init: true,
    agenceSelected: null,
    panelApp: []
    /*
    personas: [
      {
        id: "b2be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Adequation (interne)"
      },
      {
        id: "b5be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Assistant"
      },
      {
        id: "b6be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Commanditaire"
      },
      {
        id: "b3be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Commercial"
      },
      {
        id: "b1be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Développeur"
      },
      {
        id: "b4be8e31-12c9-eb11-831e-005056b21ef4",
        nom: "Directeur"
      }
    ]
    */
  }),
  //  beforeMount() {
  //   this.personas = [{
  //       "id": "46fa56c1-6dc7-eb11-9575-0050b615be0f",
  //       "nom": "Adequation (interne)"
  //     },
  //     {
  //       "id": "f9cf505c-a3c7-eb11-9575-0050b615be0f",
  //       "nom": "Assistant"
  //     },
  //     {
  //       "id": "facf505c-a3c7-eb11-9575-0050b615be0f",
  //       "nom": "Commanditaire"
  //     },
  //     {
  //       "id": "1cad9800-79c7-eb11-9575-0050b615be0f",
  //       "nom": "Commercial"
  //     },
  //     {
  //       "id": "45fa56c1-6dc7-eb11-9575-0050b615be0f",
  //       "nom": "Développeur"
  //     },
  //     {
  //       "id": "f8cf505c-a3c7-eb11-9575-0050b615be0f",
  //       "nom": "Directeur"
  //     }];

  // },
  // Here we're wiring everything up.
  computed: {
    ...mapAuthGetters(["hasAdminRights"]),
    isEdition() {
      return this.id !== null;
    },
    ...mapAppFields(["isUserDrawerOpen"]),
    ...mapPersonaState({
      personaList: "list"
    }),
    // You can read more about mapping field
    // values in two of my previous articles.
    // https://markus.oberlehner.net/blog/form-fields-two-way-data-binding-and-vuex/
    // https://markus.oberlehner.net/blog/how-to-handle-multi-row-forms-with-vue-vuex-and-vuex-map-fields/
    ...mapUserCreateFormFields([
      "entity.id",
      "entity.agenceReference",
      "entity.contactFacturation",
      "entity.contactMiseAJour",
      "entity.contactSignataire",
      "entity.code",
      "entity.prenom",
      "entity.nom",
      "entity.email",
      "entity.fonction",
      "entity.numTel",
      "entity.active",
      "entity.AD",
      "entity.admin",
      "entity.agences",
      "entity.dateFinMaj",
      "entity.commentaireMaj",
      "entity.adherent",
      "entity.persona"
    ]),
    ...mapUserCreateFormMultiRowFields(["entity.acces"]),
    referenceCompanySearchList() {
      let list = this.referenceCompanySearchListBase || [];

      if (this.isEdition && this.agenceReference) {
        list = list.concat([this.agenceReference]);
      }

      return list;
    },
    linkedCompanySearchList() {
      let list = this.linkedCompanySearchListBase || [];

      if (this.isEdition) {
        list = [...list, ...this.agences];
      }

      return list;
    },
    isEmailAvailableMessage() {
      return this.isEmailAvailable
        ? []
        : [this.$t("Cette adresse email n'est pas disponible.")];
    },
    formIsValid() {
      return this.email && /\S+@\S+\.\S+/.test(this.email);
    },
    hasRight() {
      let accesUsed = _.filter(this.acces, function(a) {
        return a.used;
      });
      return accesUsed.length > 0;
    },
    groupedApplications() {
      return Object.values(_.groupBy(this.acces, "abonnement.application.id"));
    },
    getApplicationCheckboxState: () => accesList => {
      const set = new Set(accesList.map(droit => droit.used));
      return !set.has(true) ? 0 : !set.has(false) ? 1 : -1;
    },
    getCheckboxColor: () => name => {
      return name == "bloque"
        ? "rgba(241, 59, 59, 0.8)"
        : "rgba(0, 188, 212, 0.8)";
    },
    adherentNature() {
      var value =
        this.adherent == null || this.adherent.nature == null
          ? "partenaire"
          : this.adherent.nature.toLowerCase();
      return value;
    },
    adherentScope() {
      var value = this.adherent == null ? [] : this.adherent.scope;

      return value;
    },
    isAdherent() {
      var value = this.adherent == null ? false : this.adherent.isAdherent;
      return value;
    }
  },
  methods: {
    initAgenceSelected: function(applicationAccesList) {
      if (
        applicationAccesList[0].abonnement.application.monoAgence &&
        this.agenceSelected == null
      ) {
        //01 - Find agence with more right "used"
        let applicationAccesListUsed = _.filter(
          applicationAccesList,
          access => access.used
        );
        let acces = Object.values(
          _.groupBy(applicationAccesListUsed, "abonnement.agence.id")
        );
        acces.sort((a, b) => b.length - a.length);
        let currentAgence = {};
        if ((acces.length > 0) & (acces[0].length > 0)) {
          currentAgence = acces[0][0].abonnement.agence;
        }
        //02 - set agenceSelected
        this.agenceSelected = currentAgence;
      }
    },
    groupedAccesByAgence: function(applicationAccesList) {
      if (applicationAccesList[0].abonnement.application.monoAgence) {
        if (this.agenceSelected != null) {
          //01 - identify if app is monoagence -> if yes filter right by societe id ( from soieteSelected)
          let that = this;
          let applicationAccesListTrue = {};
          applicationAccesListTrue = _.filter(
            applicationAccesList,
            access => access.abonnement.agence.id == that.agenceSelected.id
          );
          let applicationAccesListFalse = {};
          applicationAccesListFalse = _.filter(
            applicationAccesList,
            access => access.abonnement.agence.id != that.agenceSelected.id
          );
          applicationAccesListFalse.forEach(access => {
            access.used = false;
          });
          applicationAccesList = [...applicationAccesListTrue];
        } else {
          applicationAccesList = {};
        }
      }

      let acces = Object.values(
        _.groupBy(applicationAccesList, "abonnement.agence.id")
      );
      return acces.map(singularAcces => ({
        agence: singularAcces[0].abonnement.agence,
        accesByDroit: _.groupBy(singularAcces, "droit.id")
      }));
    },
    ...mapCompanyActions({
      fetchCompanies: FETCH
    }),
    ...mapUserActions({
      submitAction: SUBMIT,
      fetchUsers: FETCH,
      isLoginAvailable: IS_LOGIN_AVAILABLE
    }),
    ...mapUserCreateFormActions({
      fetchUserAvailableAcces: FETCH_USER_AVAILABLE_ACCES
    }),
    setExpirationDate(singularAcces, event) {
      singularAcces.expirationDate =
        event == null ? null : new Date(event).toISOString();
    },
    displayExpirationDate(singularAcces) {
      singularAcces.displayExpirationDate =
        singularAcces.displayExpirationDate == null ||
        singularAcces.displayExpirationDate == false
          ? true
          : false;
      this.$forceUpdate();
    },
    expansionCheckboxClick(e) {
      e.cancelBubble = true;
    },
    async checkEmailAvailability() {
      // If input value is an actual email
      if (/\S+@\S+\.\S+/.test(this.email)) {
        this.isEmailAvailable = await this.isLoginAvailable({
          currentUserId: this.isEdition ? this.id : null,
          login: this.email
        });

        if (this.isEmailAvailable === false) {
          const { data } = await UsersRepository.getByLogin({
            login: this.email
          });

          const user = data.content.entity;
          this.existingEmailUser = user;
        }
      } else {
        this.isEmailAvailable = true;
      }
    },
    onSelectCompany() {
      this.$refs.companiesAutocomplete.blur();
    },
    removeFromAgences(company) {
      if (this.agenceReference) {
        let indexAgenceReference = this.agences
          .map(agence => agence.id)
          .indexOf(this.agenceReference.id);

        if (indexAgenceReference >= 0) {
          this.agenceReference = null;
        }
      }

      this.agences = this.agences.filter(item => item !== company);
    },
    toggleAccesApp(accesList, toggle) {
      accesList.forEach(acces => {
        let toggleAcces =
          toggle == false || (toggle == true && acces.droit.code != "bloque");
        if (toggleAcces) {
          acces.used = toggle;
        }
      });
    },
    toggleAccesRight(accesList, toggle) {
      accesList.forEach(acces => (acces.used = toggle));
    },
    closeDrawer() {
      this.isUserDrawerOpen = false;
      this.resetData();
    },
    resetData() {
      Object.assign(this.$data, this.$options.data());
    },
    async submit({ callback = () => {} }) {
      this.$gtm.trackEvent({
        event: "interaction",
        category: "user",
        action: `Click (Envoie du formulaire utilisateur (${
          this.isEdition ? "Utilisateur existant" : "Nouvel utilisateur"
        }))`,
        label: `Envoie du formulaire utilisateur (${
          this.isEdition ? "Utilisateur existant" : "Nouvel utilisateur"
        })`
      });

      if (this.formIsValid) {
        this.loading = true;
        await this.submitAction();
        this.loading = false;
        callback();
      }
    },
    formatDate(date) {
      if (date.getTime() != 0) {
        return dateFormat(date, "dd/mm/yyyy");
      }

      return null;
    }
  },
  watch: {
    isUserDrawerOpen(isUserDrawerOpen) {
      if (!isUserDrawerOpen) {
        this.resetData();
      }
    },
    async agences(agences) {
      if (
        (!this.agenceReference || !this.agenceReference.id) &&
        agences.length
      ) {
        this.agenceReference = agences[0]; // Set default agence reference if not set
      }

      // Load available access
      // JNA : Disable below part, for init user
      if (!this.init) {
        this.accesLoading = true;

        await this.fetchUserAvailableAcces({
          userId: this.id,
          companyIds: agences.map(agence => agence.id)
        }).then(() => {
          this.accesLoading = false;
        });
      }
      this.init = false;
    },
    async referenceCompanySearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isReferenceCompanySearchLoading = true;

      await this.fetchCompanies({
        temporary: true,
        search: { query }
      }).then(companies => {
        if (companies) {
          this.referenceCompanySearchListBase = companies;
          this.isReferenceCompanySearchLoading = false;
        }
      });
    },
    async linkedCompanySearch(query) {
      // Items have already been requested
      if (query === null) return;

      this.isLinkedCompanySearchLoading = true;

      await this.fetchCompanies({
        temporary: true,
        search: { query }
      }).then(companies => {
        if (companies) {
          this.linkedCompanySearchListBase = companies;
          this.isLinkedCompanySearchLoading = false;
        }
      });
    }
  }
};
</script>

<style lang="scss">
.ade-forms-edit {
  &__email-message {
    display: flex;
    align-items: center;
  }

  &__update-existing {
    display: inline;
  }

  &__chip-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__listable-chip {
    &.v-chip.v-size--default {
      height: auto;
      min-height: 32px;
      padding-top: 5px;
      padding-bottom: 5px;
    }

    .v-chip__content {
      white-space: normal;
    }

    .v-chip__close.v-icon.v-icon--right {
      order: -1;
      margin-left: 0;
      margin-right: 6px !important;
    }
  }

  &__small-text {
    font-size: 0.8em;
    opacity: 0.6;
    font-weight: 500;
  }
}

.disable-events {
  pointer-events: none;
}

.contact-maj {
  background-color: rgba(0, 188, 212, 0.05);
  //width:100%;
}

.adherent {
  opacity: 0.3 !important ;
}

.adherent-selected {
  opacity: 1 !important;
}

.v-expansion-panels:not(.v-expansion-panels--accordion):not(.v-expansion-panels--tile)
  > .v-expansion-panel--active.extantion-panel-selected {
  -webkit-box-shadow: 0px 1px 8px 1px rgba(0, 0, 0, 0.56);
  -moz-box-shadow: 0px 1px 8px 1px rgba(0, 0, 0, 0.56);
  box-shadow: 0px 1px 8px 1px rgba(0, 0, 0, 0.56);
}

.dateFinMaj {
  min-width: 200px !important;
}

.expirationDate {
  min-width: 200px !important;
  // background-color: pink;
}

.red {
  color: red;
}

.icon-expanded {
  display: none;
}
.v-expansion-panel-header--active {
  .icon-expanded {
    display: block;
  }
}
</style>
